<script setup lang="ts">
import { mdiCartOutline } from '@mdi/js'
import type { Ticket } from '@/features/tickets/composables/usePricing'
import { ITicketCartResponse } from '@/features/tickets/types'
import { centsToDollars } from '@/services/currency'
import { ref, watch } from 'vue'

interface Props {
  cartSummary: ITicketCartResponse | undefined
  getTicketById: (ticketId: number) => Ticket | undefined
  isLoading: boolean
}

const props = defineProps<Props>()

const localCartSummary = ref(props.cartSummary)

watch(
  () => props.cartSummary,
  (newVal) => {
    localCartSummary.value = newVal
  }
)
</script>

<template>
  <div
    class="summary"
    :class="{ loading: isLoading }"
  >
    <v-progress-linear
      v-if="isLoading"
      indeterminate
      color="var(--color-blue-800)"
      class="progress-indicator"
    />
    <v-icon
      v-if="!localCartSummary"
      size="40"
      class="cart-icon"
      >{{ mdiCartOutline }}</v-icon
    >
    <template v-else>
      <p class="summary-title">Order summary</p>
      <ul class="summary-table">
        <li
          v-for="tier in localCartSummary.tiers"
          :key="tier.tierId"
          class="summary-ticket-row"
        >
          <span class="ticket-title">
            {{ getTicketById(tier.tierId)?.title || 'Unknown Ticket' }}
          </span>
          <span class="ticket-quantity">x{{ tier.quantity }}</span>
          <span class="ticket-price">
            {{ centsToDollars(tier.price) }}
          </span>
        </li>
      </ul>
      <v-divider />
      <ul class="subtotal-table">
        <li class="subtotal-row">
          <span class="subtotal-label">Costs</span>
          <span class="subtotal-value">
            {{ centsToDollars(localCartSummary.subtotal) }}
          </span>
        </li>
        <li class="subtotal-row">
          <span class="subtotal-label">Platform fees</span>
          <span class="subtotal-value">
            {{ centsToDollars(localCartSummary.platformFee) }}
          </span>
        </li>
        <li class="subtotal-row">
          <span class="subtotal-label">Taxes</span>
          <span class="subtotal-value"> {{ centsToDollars(localCartSummary.taxes) }} </span>
        </li>
        <li
          v-if="localCartSummary.discountAmount"
          class="subtotal-row"
        >
          <span class="subtotal-label discount-label">
            Discounts
            <span class="discount-description"> {{ localCartSummary.discountDescription }} </span>
          </span>
          <span class="subtotal-value"> -{{ centsToDollars(localCartSummary.discountAmount) }} </span>
        </li>
      </ul>
      <v-divider />
      <div class="total-row">
        <span class="total-label">Total</span>
        <span class="total-value">
          {{ centsToDollars(localCartSummary.total) }}
        </span>
      </div>
    </template>
  </div>
</template>

<style scoped lang="scss">
.summary {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 40px 20px;
  background-color: var(--color-grey-100);

  &.loading > * {
    opacity: 0.5;
  }

  ul,
  li {
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .cart-icon {
    align-self: center;
    justify-self: center;
    margin: auto;
  }

  .summary-title {
    font-size: 20px;
    line-height: 26px;
    font-weight: 500;
    color: var(--color-dark-800);
  }

  .summary-table {
    display: grid;
    grid-template-columns: 1fr auto auto;
    gap: 10px;

    .summary-ticket-row {
      display: grid;
      grid-template-columns: subgrid;
      grid-column: span 3;
      align-items: center;
      font-size: 16px;
      line-height: 24px;
      color: var(--color-dark-800);

      .ticket-title {
        font-weight: 500;
      }

      .ticket-quantity {
        text-align: center;
      }

      .ticket-price {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        text-align: right;
        font-weight: 500;

        .ticket-old-price {
          font-size: 14px;
          line-height: 20px;
          color: var(--color-grey-300);
          text-decoration: line-through;
        }
      }
    }
  }

  .subtotal-table {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 10px;

    .subtotal-row {
      display: contents;

      .subtotal-label {
        font-size: 16px;
        line-height: 24px;
        color: var(--color-dark-800);

        &.discount-label {
          display: flex;
          flex-direction: column;

          .discount-description {
            font-size: 12px;
            line-height: 16px;
            color: var(--color-grey-300);
          }
        }
      }

      .subtotal-value {
        font-size: 16px;
        line-height: 24px;
        text-align: right;
        color: var(--color-dark-800);
      }
    }
  }

  .total-row {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 10px;
    font-size: 16px;
    line-height: 24px;
    font-weight: 700;
    color: var(--color-dark-800);

    .total-value {
      text-align: right;
    }
  }

  .progress-indicator {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    opacity: 1 !important;
  }
}
</style>
